/**
 * Every line Phase 5 puts on a screen, in one file — and the two functions that choose
 * which line to say.
 *
 * ========================= WHY THE COPY IS IN A MODULE =========================
 * Master plan §7 sets four rules for every message the free game shows the player, and
 * they are rules about the WHOLE SET rather than about any one string:
 *
 *   1. it is about what just happened to THIS player;
 *   2. one line;
 *   3. one tap, into the matching Train card;
 *   4. no countdown, no scarcity, nothing third-party.
 *
 * A rule about a set cannot be checked in the components that draw its members. So the
 * set lives here, `tests/phase5-copy.test.ts` reads it, and `precision-copy` can look at
 * one file to see that nothing on the seat card, the Ledger or the hand-end verdict has
 * quietly started claiming an accuracy the product does not have.
 *
 * ------------------------- AND WHY THE JUDGE IS "OUR MODEL" --------------------
 * The brief's example line is "The chart shoves here". It is not written that way here,
 * and the difference is not a preference. The thing that grades a hero's decision in
 * play is `Game`'s reference profile — a heuristic, priced by Monte Carlo, the same one
 * the bots play. The solved charts are in the TRAINER. `HandHistory` and `StatsPanel`
 * both already say "our model … a heuristic, not a solve" in as many words, and a
 * verdict on the felt that said "the chart" would be the one place in the product where
 * the heuristic was dressed as a solve — on the busiest screen, in the biggest type.
 * So the verdict says what actually judged it, and the drill it links to is where the
 * solved charts really are. That is the whole pitch, told honestly.
 * ===============================================================================
 */
import type { Street } from "@engine/types";
import { TRAIN_CARDS, PLAY_CARDS, type TrainCard } from "./poker-cards";
import { BJ_NEXT_DRILL } from "./bj-copy";

/* ============================================================ the seat card === */

/**
 * The fairness page's requirement, in the one place it is now met.
 *
 * `FAIRNESS.md` §"reach the proof" says the session commitment is on screen FROM
 * SIT-DOWN. Until this phase the only place it appeared was `HandHistory`, below the
 * fold of the table screen — true, and not "from sit-down". The seat card is now that
 * place, and this is the sentence beside it. It promises exactly what the page can
 * deliver and nothing more: the seed is not published until the session ends.
 */
export const COMMITMENT_LINE =
  "This deck was decided before you sat down. Keep this number and check it on the " +
  "fairness page when you stand up.";

export const SEAT_COPY = {
  /** Above the door. */
  finding: "Finding you a seat",
  /** While the buy-in is posting. Never a spinner's worth of words. */
  buying: "Buying your seat",
  /** While the cast sits down. */
  seating: "Taking their seats",
  ready: "Deal",
  commitmentLabel: "Session commitment",
  commitmentLine: COMMITMENT_LINE,
  /** The can't-afford state, which is Phase 4's flow reached from inside the card. */
  cantAfford: "Not enough Bucks for this seat",
  askBarkeep: "Ask the Barkeep",
  pickSmaller: "Pick a shorter seat on the Play tab — Buster Bucks are practice money and they refill.",
  leave: "Back",
} as const;

/* ================================================================ the Ledger === */

export const LEDGER_COPY = {
  title: "The Ledger",
  netUp: "You finished up",
  netDown: "You finished down",
  netFlat: "You finished level",
  hands: "Hands",
  bb100: "bb/100",
  biggestPot: "Biggest pot",
  evLost: "EV lost",
  accuracy: "Accuracy",
  evGivenUp: "EV given up",
  /** Phase 9: in the EV cell, for a player without Blackjack Pro. */
  evLocked: "Blackjack Pro",
  place: "Finished",
  prize: "Prize",
  castTitle: "Who you were playing",
  /**
   * The reveal's own guardrail, kept word for word from the panel this screen replaces.
   *
   * `chartAdherence` is the only figure shown about a bot because it is the only one
   * that can be verified by counting. The sentence was reviewed in Phase 2b as part of
   * `Play.tsx`'s reveal; moving the panel must not quietly reword it.
   */
  castNote:
    "Seats show a name, not a playing style — working out the style is the skill. " +
    "Here is what each of them was.",
  adherenceNote:
    "The percentage is how often that seat followed the solved push/fold chart — " +
    "counted, not claimed. Everywhere else it played our heuristic.",
  again: "Play again",
  saloon: "Back to the saloon",
  drillTitle: "Sharpen this",
  /** Shown instead of a stat row when the sitting produced no completed hand. */
  noHands: "You left before a hand finished. Your buy-in came straight back.",
  /** The seat cost nothing, so nothing settled. A drill-shaped table. */
  unfunded: "No Bucks changed hands",
  /** It cost something and the settlement is on its way. Never printed as a zero. */
  settling: "Settling up",
  /** It cost something and the settlement did not land. Also never a zero. */
  unsettled: "This sitting has not settled yet — Play again will retry it",
  /** No hand finished, and the money has not come back yet. */
  noHandsYet: "You left before a hand finished. Your buy-in is on its way back.",
  /** Phase 9: the blackjack sitting's seed, shown only once the sitting is over. */
  proofTitle: "Where these cards came from",
  proofLine:
    "The commitment was on the seat card before the first card came out of the shoe. " +
    "The seed it commits to is shown now, after the last one — every hand of the sitting " +
    "was dealt from it, so you can deal them again yourself.",
  proofCommitment: "Commitment, shown before",
  proofSeed: "Seed, revealed after",
  proofCheck: "Check this shoe",
} as const;

/* ============================================================== the verdict === */

/** What the reference model priced, as `Game` records it on a hero action. */
export interface GradedAction {
  seat: number;
  street: Street;
  graded?: { evLost: number; bestLabel: string; chosenLabel: string };
}

/**
 * A model label turned into a verb.
 *
 * The labels come from `engine/bot/policy.ts` and are already English — "fold",
 * "check", "all in", "call 2.5bb", "raise to 8bb". They are nouns-as-phrases, and a
 * verdict reads better as a verb, so this is the whole of the translation. Anything
 * unrecognised is passed through rather than guessed at: an unknown label printed
 * verbatim is a line that reads slightly oddly, and a guessed one is a line that says
 * the model did something it did not.
 */
export function verbOf(label: string): string {
  const l = label.trim().toLowerCase();
  if (l === "fold") return "folds";
  if (l === "check") return "checks";
  if (l === "all in" || l === "allin") return "shoves";
  if (l.startsWith("call")) return "calls";
  if (l.startsWith("raise to ")) return `raises to ${l.slice("raise to ".length)}`;
  if (l.startsWith("bet")) return `bets ${l.slice(3).trim()}`.trim();
  return l;
}

/** Big blinds, at the precision a player can act on. One decimal, never trailing ".0". */
export function bbText(chips: number, bigBlind: number): string {
  if (!(bigBlind > 0)) return "0";
  const v = chips / bigBlind;
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * The floor under which a verdict says nothing.
 *
 * The judge is a Monte-Carlo heuristic run at a few hundred trials, so the difference
 * between two close options is partly noise, and a line that fires on noise teaches the
 * player to ignore the line. Half a big blind is the smallest number worth interrupting
 * somebody for; below it the hand is recorded in the history and the Ledger's totals,
 * where it belongs, and nothing is said on the felt.
 */
export const VERDICT_FLOOR_BB = 0.5;

export interface Verdict {
  /** The whole line, minus the drill's own name, which the component links. */
  text: string;
  /** A Train card id. Guaranteed to exist — see the module-load check below. */
  drill: string;
  /** For the report and the tests: what it was measured at. */
  evLostBb: number;
  street: Street;
}

/**
 * One line at hand end, or nothing.
 *
 * Nothing is the common case and it is the important one. This returns null when:
 *   - the hero was not in the hand, or took no action in it;
 *   - none of the hero's actions was graded (a table with grading off);
 *   - the worst disagreement is under `VERDICT_FLOOR_BB`, which includes every hand
 *     the hero folded preflop and was right to.
 *
 * When it does fire it names the ONE worst decision of the hand — not a list, not a
 * total. A hand has at most one verdict, whatever else happened in it.
 */
export function verdictLine(hand: {
  heroSeat: number;
  actions: readonly GradedAction[];
  bigBlind: number;
  /** The Play card this sitting came from, which picks the drill. */
  cardId?: string | null;
  seats?: number;
  stackBb?: number;
  format?: "cash" | "tournament";
}): Verdict | null {
  if (hand.heroSeat < 0) return null;
  /* A table with no big blind has no big blinds to cost anything, and dividing by a
     substituted 1 would print a number in a unit the table does not have. */
  if (!(hand.bigBlind > 0)) return null;
  let worst: GradedAction | null = null;
  for (const a of hand.actions) {
    if (a.seat !== hand.heroSeat || !a.graded) continue;
    if (!worst || a.graded.evLost > worst.graded!.evLost) worst = a;
  }
  if (!worst?.graded) return null;
  /*
   * DISAGREEMENT, verified rather than assumed.
   *
   * The floor is what makes this a rare line, and above it the model has by definition
   * priced something else higher — but "our model folds there" printed under a hand the
   * hero also folded would be the one sentence on the felt that is simply false, so the
   * labels are compared as well. They are the model's own strings, from `policy.ts`.
   */
  if (worst.graded.bestLabel === worst.graded.chosenLabel) return null;
  const bb = worst.graded.evLost / hand.bigBlind;
  if (!(bb >= VERDICT_FLOOR_BB)) return null;

  const drill = drillFor(hand);
  return {
    text: `Our model ${verbOf(worst.graded.bestLabel)} there — ${bbText(worst.graded.evLost, hand.bigBlind)} bb.`,
    drill: drill.id,
    evLostBb: bb,
    street: worst.street,
  };
}

/* ============================================================ the next drill === */

export const STREETS: readonly Street[] = ["preflop", "flop", "turn", "river"];

/**
 * A street as a phrase, and a spot as a phrase plus a position.
 *
 * The Barkeep's line reads this, so it has to survive being dropped into the middle of
 * a sentence: "Most of what you gave up last time went **on the turn**." A position is
 * included when there is one, because "preflop from the cutoff" is a spot a player can
 * picture and "preflop" is a third of a game.
 */
export function streetPhrase(street: Street): string {
  return street === "preflop" ? "preflop" : `on the ${street}`;
}

export function spotLabel(street: Street, position?: string | null): string {
  const base = streetPhrase(street);
  return position ? `${base} from the ${position}` : base;
}

/** What a sitting looked like, as the Ledger and the Barkeep read it. */
export interface SittingSummary {
  kind: "poker-cash" | "poker-tournament" | "blackjack";
  hands: number;
  /** In the table's own chips. */
  net: number;
  bb100: number | null;
  biggestPot: number | null;
  evLost: number;
  evLostByStreet: Record<Street, number>;
  bigBlind: number;
  seats: number;
  stackBb: number;
  /**
   * Cash or tournament.
   *
   * Carried even though `kind` implies it, because `drillFor` reads `format` and a
   * summary that left it out sent every CUSTOM tournament — the ones with no card to
   * name a drill — to the cash table's drill instead of the bubble's.
   */
  format?: "cash" | "tournament";
  /** The Play card this sitting came from. */
  cardId?: string | null;
  /** Phase 5 box 6: what the Barkeep's line reads. */
  biggestLostPot?: number | null;
  worstSpot?: { street: Street; label: string } | null;
  /** Tournament only, and derived by counting rather than by the engine. */
  placement?: { place: number; of: number } | null;
  prize?: number | null;
  /** Blackjack only. */
  accuracy?: number | null;
  evGivenUp?: number | null;
  /**
   * PHASE 9, blackjack only: the viewer does not hold Blackjack Pro (master plan §9), so
   * the Ledger names the unlock where the EV figure would be. The figure is still here
   * — it decides whether a drill is suggested — and is simply not printed.
   */
  exactLocked?: boolean;
}

/** The street the most EV went on, or null when nothing was lost anywhere. */
export function worstStreet(by: Record<Street, number>): Street | null {
  let best: Street | null = null;
  for (const s of STREETS) if ((by[s] ?? 0) > 0 && (!best || by[s]! > by[best]!)) best = s;
  return best;
}

/**
 * Which Train card fits this table.
 *
 * The library drills ONE thing — preflop shove-or-fold — at a selection of table size,
 * ICM context and stack depth, and every Train card is a point in that selection. So
 * the choice here is a choice of POINT, made from the table that was actually played:
 * heads-up sends you to the heads-up card, a short-stacked tournament to the bubble
 * card, ten big blinds or fewer to Short Stacks, and everything else to the basics.
 *
 * `cardId` is consulted first because a Play card already names the drill it sharpens
 * and that mapping was reviewed in Phase 2b; the shape of the table is the fallback for
 * a Custom Table, which names none.
 */
export function drillFor(t: {
  cardId?: string | null; seats?: number; stackBb?: number; format?: "cash" | "tournament";
}): TrainCard {
  const named = PLAY_CARDS.find((c) => c.id === t.cardId)?.drill;
  const byName = TRAIN_CARDS.find((c) => c.id === named && !c.verifying && !c.build);
  if (byName) return byName;
  const pick = (id: string) => TRAIN_CARDS.find((c) => c.id === id)!;
  /*
   * The order is the note's order, and it was not at first.
   *
   * `gauntlet` came before `bubble`, so a ten-big-blind BUBBLE — which is the whole of
   * Bubble Pressure, and the most specific spot in the library — was answered with
   * Short Stacks. ICM is the more particular fact about a table than depth is, so the
   * tournament test goes first and Short Stacks catches the shallow CASH tables that
   * are left.
   */
  if ((t.seats ?? 6) === 2) return pick("headsup");
  if (t.format === "tournament" && (t.stackBb ?? 100) <= 15) return pick("bubble");
  if ((t.stackBb ?? 100) <= 10) return pick("gauntlet");
  return pick("pushfold");
}

export interface NextDrill {
  /** A Train card id that exists. */
  id: string;
  title: string;
  href: string;
  /** One line, about this sitting. */
  because: string;
}

/**
 * The Ledger's one line of marketing: what this sitting showed, and where to sharpen it.
 *
 * Returns null when there is nothing true to say — no hands, or no EV given up. A
 * player who sat down, played five clean hands and stood up is told nothing, which is
 * the same rule the Barkeep's line follows and the reason either is worth reading.
 *
 * The STREET decides what the line says; the TABLE decides which card it points at.
 * Those are two different questions and conflating them is how a product ends up
 * offering a preflop drill for a turn leak and calling it personalisation. When the
 * loss was postflop the line says so plainly and says what the trainer drills today —
 * the library has no postflop drill and this copy does not pretend otherwise.
 */
export function nextDrill(s: SittingSummary): NextDrill | null {
  if (s.hands <= 0) return null;
  const card = drillFor(s);
  const href = `/poker/train/run?card=${card.id}`;
  const base = { id: card.id, title: card.title, href };

  if (s.kind === "blackjack") {
    if (!(s.evGivenUp && s.evGivenUp > 0)) return null;
    /*
     * PHASE 8: the blackjack room's OWN drill. Until this phase the room had no Train
     * cards that did anything, so a blackjack sitting was pointed at poker's push/fold
     * card — a drill for a different game. It points at the Basic Strategy Drill now.
     */
    return {
      id: BJ_NEXT_DRILL.id, title: BJ_NEXT_DRILL.title, href: BJ_NEXT_DRILL.href,
      because: "Every hand where you left value on the table was a basic-strategy spot.",
    };
  }

  const street = worstStreet(s.evLostByStreet);
  if (!street || !(s.evLost > 0)) return null;
  if (street === "preflop") {
    return { ...base, because: `Most of what you gave up went preflop, ${s.seats}-handed at ${s.stackBb} big blinds.` };
  }
  return {
    ...base,
    because: `Most of what you gave up went on the ${street}. Preflop is what the trainer drills today.`,
  };
}

/* ========================================================== the seat's hook === */

/** The one line §7 allows on the seat card. Null for a player with no history. */
export interface SeatHook { text: string; drill: string; title: string; href: string }

/**
 * What to say to this player while they sit down, or nothing.
 *
 * Two sources, in order of how recent and how specific they are: the verdict from the
 * last hand they were graded on, and — failing that — the drill the card they are
 * about to play sharpens. A player with neither is told NOTHING, which is most of the
 * point: a first-time player meets a card with a door, a price and a hash on it, and no
 * pitch at all.
 */
export function seatHook(input: {
  lastVerdict?: { text: string; drill: string } | null;
  cardId?: string | null;
  hasHistory?: boolean;
}): SeatHook | null {
  const asCard = (id: string, text: string): SeatHook | null => {
    const c = TRAIN_CARDS.find((x) => x.id === id && !x.verifying && !x.build);
    return c ? { text, drill: c.id, title: c.title, href: `/poker/train/run?card=${c.id}` } : null;
  };
  if (input.lastVerdict) {
    return asCard(input.lastVerdict.drill, `Last hand: ${input.lastVerdict.text}`);
  }
  if (!input.hasHistory) return null;
  const card = PLAY_CARDS.find((c) => c.id === input.cardId);
  if (!card?.drill) return null;
  return asCard(card.drill, `${card.title} is where ${TRAIN_CARDS.find((t) => t.id === card.drill)?.title.toLowerCase() ?? ""} pays.`);
}

/* ==================================================================== checks === */

/**
 * Every drill link in this file points at a Train card that exists — checked at module
 * load, which is the pattern Phase 2b established for `PLAY_CARDS`.
 *
 * A dead drill link is the one failure mode of §7 that a screenshot cannot show: the
 * line renders, the tap happens, and the trainer opens on whatever its own fallback
 * chooses. It has to fail loudly at load or it fails quietly at the player.
 */
const REACHABLE = ["pushfold", "bubble", "headsup", "gauntlet"] as const;
for (const id of REACHABLE) {
  const c = TRAIN_CARDS.find((t) => t.id === id);
  if (!c) throw new Error(`Phase 5 copy links to Train card "${id}", which does not exist.`);
  if (c.verifying) throw new Error(`Phase 5 copy links to "${id}", which is still verifying and does nothing.`);
  if (c.build) throw new Error(`Phase 5 copy links to "${id}", which is the Build screen and not a drill.`);
}
/* And every Play card's own drill, since `drillFor` will return one. */
for (const c of PLAY_CARDS) {
  if (!c.drill) continue;
  const t = TRAIN_CARDS.find((x) => x.id === c.drill);
  if (!t) throw new Error(`Play card "${c.id}" sharpens "${c.drill}", which is not a Train card.`);
}
